import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { useForm, FormProvider } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { Address } from "@/types";
import { PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";
import AddressInput from "@/features/facility/components/ContactDetails/forms/inputs/AddressInput";
import { OptionalAddressSchema } from "./validation";

const AddressFormSchema = z.object({
  mainAddress: OptionalAddressSchema,
  mailingAddress: OptionalAddressSchema
});

type FieldValues = z.infer<typeof AddressFormSchema>;

interface AddressEditFormProps extends EditProps {
  mainAddress?: Address | null;
  mailingAddress?: Address | null;
}

function prepareAddressForForm(address: Address | null | undefined) {
  return {
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    city: address?.city || "",
    countryCode: address?.countryCode || "",
    postalCode: address?.postalCode || "",
    state: address?.state || "",
    addressType: ""
  };
}

const defaultErrorMessage = "Failed to update addresses";

export default function AddressEditForm({
  facilityId,
  mainAddress: initialMainAddress,
  mailingAddress: initialMailingAddress,
  onCancel,
  onSuccess,
  onError
}: AddressEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      mainAddress: prepareAddressForForm(initialMainAddress),
      mailingAddress: prepareAddressForForm(initialMailingAddress)
    },
    resolver: zodResolver(AddressFormSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6" data-testid="address-edit-form">
        <AddressInput name="mainAddress" label="Main" />
        <AddressInput name="mailingAddress" label="Mailing" />
        <div className="flex flex-wrap justify-end gap-1 items-center">
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
